import { Input } from '@/components/ui/input';
import { useState, useEffect } from 'react';

interface TargetROASInputProps {
  targetRoas: number | undefined;
  setTargetROAS: (value: number) => void;
  disabled?: boolean;
  showPercentage?: boolean;
}

export const TargetROASInput: React.FC<TargetROASInputProps> = ({
  targetRoas,
  setTargetROAS,
  disabled = false,
  showPercentage = false,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [error, setError] = useState<string>('');

  const ratioToPercentage = (ratio: number | undefined): number => {
    if (ratio === undefined) return 0;
    return ratio * 100;
  };

  const percentageToRatio = (percentage: number): number => {
    return percentage / 100;
  };

  useEffect(() => {
    if (showPercentage) {
      setInputValue(targetRoas ? ratioToPercentage(targetRoas).toString() : '');
    } else {
      setInputValue(targetRoas?.toString() || '');
    }
  }, [targetRoas, showPercentage]);

  const validateAndApply = (value: string): boolean => {
    if (value === '') {
      setTargetROAS(0);
      setError('');
      return true;
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return false;
    }
    if (showPercentage) {
      if (numValue < 100) {
        setError('Target ROAS must be at least 100%');
        return false;
      }
      if (numValue > 10000) {
        setError('Target ROAS cannot exceed 10,000%');
        return false;
      }
      setTargetROAS(percentageToRatio(numValue));
      setError('');
      return true;
    } else {
      if (numValue < 1.0) {
        setError('Target ROAS must be at least 1.0 (100%)');
        return false;
      }
      if (numValue > 100.0) {
        setError('Target ROAS cannot exceed 100.0 (10,000%)');
        return false;
      }
      setTargetROAS(numValue);
      setError('');
      return true;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (value === '') {
      setTargetROAS(0);
      setError('');
      return;
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    if (showPercentage) {
      if (numValue >= 100 && numValue <= 10000) {
        setTargetROAS(percentageToRatio(numValue));
        setError('');
      }
    } else {
      if (numValue >= 1.0 && numValue <= 100.0) {
        setTargetROAS(numValue);
        setError('');
      }
    }
  };

  const handleBlur = () => {
    validateAndApply(inputValue);
  };

  const placeholder = showPercentage ? '350' : '3.50';
  const suffix = showPercentage ? '%' : '';
  const min = showPercentage ? '100' : '1.0';
  const max = showPercentage ? '10000' : '100.0';

  return (
    <div>
      <div className='flex items-center gap-3'>
        <div className='w-1/3 relative'>
          <Input
            type="number"
            step="0.01"
            min={min}
            max={max}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={`!ring-inset !ring-transparent text-gray-900 pr-8 ${error ? 'border-red-500' : ''}`}
          />
          {suffix && (
            <span className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none'>
              {suffix}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500">
          {showPercentage
            ? 'e.g. 350% means $3.50 back for every $1 spent.'
            : 'e.g. 3.5 means 350% return on ad spend.'}
        </p>
      </div>
      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};
